import { useState, useCallback } from 'react';
import {
  ServiceRequest,
  ServiceRequestResponse,
  SearchServiceRequestsRequest,
  ACLAuthorizationRequest,
  SearchServiceRequestsPostContentTypeEnum,
  CreateServiceRequestContentTypeEnum,
} from '@/api/bol';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { getServiceRequestsApi } from '@/services/bolApiClient';
import { extractErrorMessage, formatOperationError } from '@/utils/errorUtils';
import { getLogisticsObjectDetailPath } from '@/types/logisticsObject';
import { getEffectiveLogisticsObjectSearchQuery } from '@/utils/searchDefaults';

export interface UseServiceRequestsReturn {
  serviceRequests: ServiceRequestResponse[];
  loading: boolean;
  searchError: string | null;
  selectedServiceRequest: ServiceRequestResponse | null;
  setSelectedServiceRequest: (serviceRequest: ServiceRequestResponse | null) => void;
  fetchServiceRequests: (page?: number, pageSize?: number, searchBody?: SearchServiceRequestsRequest) => Promise<boolean>;
  createServiceRequest: (serviceRequestData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => Promise<void>;
  deleteServiceRequest: (serviceRequestId: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const useServiceRequests = (): UseServiceRequestsReturn => {
  const [items, setItems] = useState<ServiceRequestResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedServiceRequest, setSelectedServiceRequest] = useState<ServiceRequestResponse | null>(null);
  const [total, setTotal] = useState(0);

  const { showSuccess, showError } = useSnackbarNotification();

  const fetchServiceRequests = useCallback(async (page: number = 0, pageSize: number = 10, searchBody?: SearchServiceRequestsRequest): Promise<boolean> => {
    const normalizedSearchBody = getEffectiveLogisticsObjectSearchQuery(searchBody);
    setLoading(true);
    setSearchError(null);

    try {
      const api = getServiceRequestsApi();
      const response = await api.searchServiceRequestsPost({
        contentType: SearchServiceRequestsPostContentTypeEnum.ApplicationJson,
        searchServiceRequestsRequest: normalizedSearchBody,
        limit: pageSize,
        offset: page * pageSize,
      });

      setItems(response.results || []);
      setTotal(response.pagination?.total || 0);
      return true;
    } catch (err: unknown) {
      const errorMessage = await extractErrorMessage(err, 'Failed to fetch service-requests');
      setSearchError(errorMessage);
      showError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const createServiceRequest = useCallback(async (serviceRequestData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => {
    try {
      const api = getServiceRequestsApi();

      const createdServiceRequest = await api.createServiceRequest({
        contentType: CreateServiceRequestContentTypeEnum.ApplicationJson,
        xBOLMemberIdentifier: memberIdentifier,
        serviceRequest: serviceRequestData as ServiceRequest,
        source: true,
        xBOLACLAuthorizations: aclAuthorizations ? JSON.stringify(aclAuthorizations) : undefined,
      });

      const createdServiceRequestId = createdServiceRequest?.id;

      showSuccess(
        'ServiceRequest created successfully',
        createdServiceRequestId
          ? {
              link: {
                label: 'View details',
                href: getLogisticsObjectDetailPath('service-request', createdServiceRequestId),
              },
            }
          : undefined,
      );
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Create service-request', 'Failed to create service-request');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  const deleteServiceRequest = useCallback(async (serviceRequestId: string) => {
    try {
      const api = getServiceRequestsApi();
      await api.deleteServiceRequestById({ serviceRequestId });
      showSuccess('ServiceRequest deleted successfully');
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Delete service-request', 'Failed to delete service-request');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  return {
    serviceRequests: items,
    loading,
    searchError,
    selectedServiceRequest,
    setSelectedServiceRequest,
    fetchServiceRequests,
    createServiceRequest,
    deleteServiceRequest,
    setTotal,
    total,
  };
};
